<template>
  <div>
    <div
        class="tw-text-white tw-p-4 tw-flex tw-justify-between tw-items-center"
        :style="{ background: primaryBackgroundColour }"
    >
      <button
          class="dynamic-button-hover tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-flex tw-items-center"
          :style="buttonStyles"
          style="max-width: 40vw"
          @click="toggleEditForm"
      >
        <div
            class="tw-cursor-pointer hover:tw-text-blue-300 tw-px-4"
        >
          <h1 class="tw-text-lg tw-font-semibold">
            {{ quoteStore.quoteObj.name }}
          </h1>
          <p class="tw-text-sm">
            {{ quoteStore.quoteObj.customer_name }}
          </p>
        </div>

        <svg
            :class="[isEditing ? 'tw-rotate-180' : 'tw-rotate-0']"
            class="tw-h-4 tw-w-4 tw-transform tw-transition-transform tw-duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
        >
          <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <div class="tw-flex tw-items-center">
        <button
            v-if="mode"
            class="dynamic-button-hover tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-mr-4"
            :style="buttonStyles"
            @click="$emit('change-mode')"
        >
          Return To Quote
        </button>

        <div class="tw-relative">
          <button
              class="dynamic-button-hover tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-flex tw-items-center"
              :style="buttonStyles"
              @click="expanded = !expanded"
          >
            <span class="tw-mr-2">Quote Value: £{{ totalQuoteValue.toFixed(2) }}</span>
            <svg
                :class="[expanded ? 'tw-rotate-180' : 'tw-rotate-0']"
                class="tw-h-4 tw-w-4 tw-transform tw-transition-transform tw-duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
              <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <transition name="fade">
            <div
                v-if="expanded"
                class="tw-absolute tw-right-0 tw-mt-2 tw-bg-white tw-shadow-md tw-rounded-md tw-w-64 tw-overflow-hidden tw-z-10"
            >
              <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                <thead class="tw-bg-gray-50">
                <tr>
                  <th class="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                    Frame
                  </th>
                  <th class="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                    Value
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr
                    v-for="(frame, index) in frames"
                    :key="frame.id"
                >
                  <td class="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-900">
                    Frame #{{ index + 1 }}
                  </td>
                  <td class="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                    £{{ frame.price * frame.quantity }}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </transition>
        </div>

        <button
            class="dynamic-button-hover tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-ml-4"
            :style="buttonStyles"
            @click="$emit('email-quote')"
        >
          📧
        </button>
        <button
            class="dynamic-button-hover tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-ml-4"
            :style="buttonStyles"
            @click="$emit('view-quote')"
        >
          📄
        </button>
        <button
            v-if="coreStore.settings.is_g4t"
            class="dynamic-button-hover tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-ml-4 text-white"
            :style="buttonStyles"
            title="Purchase Quote"
            @click="purchaseQuote"
        >
          Purchase From Glaze4Trade
        </button>
        <button
            v-if="!mode"
            class="dynamic-button-hover tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-ml-4"
            :style="buttonStyles"
            @click="$emit('add-frame')"
        >
          ➕ Add New Frame
        </button>
      </div>
    </div>

    <transition name="fade">
      <div
          v-if="isEditing"
          class="tw-p-6 tw-bg-gray-50 tw-shadow-md"
      >
        <h2 class="tw-text-xl tw-font-bold tw-mb-4">
          Edit Quote Details
        </h2>
        <div
            v-if="editableQuote"
            class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4"
        >
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Quote Name</label>
            <input
                v-model="editableQuote.name"
                type="text"
                class="tw-mt-1 tw-block tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-p-2"
            >
          </div>
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Customer Name</label>
            <input
                v-model="editableQuote.customer_name"
                type="text"
                class="tw-mt-1 tw-block tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-p-2"
            >
          </div>
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Customer Email</label>
            <input
                v-model="editableQuote.customer_email"
                type="email"
                class="tw-mt-1 tw-block tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-p-2"
            >
          </div>
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Customer Phone</label>
            <input
                v-model="editableQuote.customer_phone"
                type="tel"
                class="tw-mt-1 tw-block tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-p-2"
            >
          </div>

          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Address Line 1</label>
            <input
                v-model="editableQuote.address_line_one"
                type="text"
                class="tw-mt-1 tw-block tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-p-2"
            >
          </div>
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Address Line 2</label>
            <input
                v-model="editableQuote.address_line_two"
                type="text"
                class="tw-mt-1 tw-block tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-p-2"
            >
          </div>
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">County</label>
            <input
                v-model="editableQuote.address_county"
                type="text"
                class="tw-mt-1 tw-block tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-p-2"
            >
          </div>
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Postcode</label>
            <input
                v-model="editableQuote.address_postcode"
                type="text"
                class="tw-mt-1 tw-block tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-p-2"
            >
          </div>
        </div>

        <div class="tw-mt-6 tw-flex tw-justify-end">
          <button
              class="tw-bg-gray-300 hover:tw-bg-gray-400 tw-text-gray-800 tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-mr-2"
              @click="cancelEdit"
          >
            Cancel
          </button>
          <button
              class="dynamic-button-hover tw-font-bold tw-py-2 tw-px-4 tw-rounded"
              :style="buttonStyles"
              @click="saveQuote"
          >
            Save Changes
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {useQuoteStore} from "@/stores/QuoteStore";
import {useCoreStore} from "@/stores/CoreStore";


export default {
  inject: ['quoteBuilderService'],
  props: ['mode'],
  emits: ['add-frame', 'change-mode', 'email-quote', 'view-quote'],
  setup() {
    const quoteStore = useQuoteStore();
    const coreStore = useCoreStore();

    return {quoteStore, coreStore};
  },
  data() {
    return {
      expanded: false,
      isEditing: false,
      editableQuote: null,
      frames: {},
    }
  },
  computed: {
    primaryBackgroundColour() {
      if (this.quoteStore.settings && this.quoteStore.settings.primary_colour) {
        return this.quoteStore.settings.primary_colour;
      }
      return '#6495ED'
    },
    totalQuoteValue() {
      let totalValue = 0;
      if (this.quoteStore.frames) {
        this.quoteStore.frames.forEach(function (item) {
          totalValue += (parseFloat(item.price) * item.quantity)
        })
      }
      return totalValue
    },
    buttonStyles() {
      const darkenColor = (color, percent) => {
        let f = parseInt(color.slice(1), 16), t = percent < 0 ? 0 : 255, p = percent < 0 ? percent * -1 : percent,
            R = f >> 16, G = f >> 8 & 0x00FF, B = f & 0x0000FF;
        return "#" + (0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B)).toString(16).slice(1);
      };

      const getContrastColor = (hexcolor) => {
        hexcolor = hexcolor.replace("#", "");
        const r = parseInt(hexcolor.substr(0, 2), 16);
        const g = parseInt(hexcolor.substr(2, 2), 16);
        const b = parseInt(hexcolor.substr(4, 2), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? 'black' : 'white';
      };

      const buttonBgColor = darkenColor(this.primaryBackgroundColour, -0.3);

      return {
        backgroundColor: buttonBgColor,
        color: getContrastColor(buttonBgColor),
      };
    }
  },
  mounted() {
    this.frames = this.quoteStore.frames
  },
  methods: {
    quit() {
      window.location = '/admin/quotes/';
    },
    toggleEditForm() {
      this.isEditing = !this.isEditing;
      if (this.isEditing) {
        this.editableQuote = JSON.parse(JSON.stringify(this.quoteStore.quoteObj));
      }
    },
    async saveQuote() {
      this.quoteStore.quoteObj = this.editableQuote;
      await this.quoteBuilderService.updateQuote()

      this.isEditing = false;
      this.editableQuote = null;
    },
    cancelEdit() {
      this.isEditing = false;
      this.editableQuote = null;
    },
    async purchaseQuote() {
      try {
        let response = await this.quoteBuilderService.purchaseQuote()
        const data = response.data;

        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else {
          console.error('API did not return a redirect URL.');
          alert('Failed to initiate purchase: No redirect URL received.');
        }
      } catch (error) {
        console.error('Error purchasing quote:', error);
        // Display an error message to the user
        alert('An error occurred while trying to purchase the quote. Please try again.');
      }
    }
  }
};
</script>